import React from 'react';
import dayjs from 'dayjs';
import { I18n } from 'react-redux-i18n';
import { CalendarPlus, Clock, History } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiInstant, apiWallClock } from '../../../utils/time/apiDateTime';
import { formatDateTime } from '../../../utils/time/formatDateTime';
import { formatTimeElapsed } from '../../../utils/time/formatTimeElapsed';
import styles from './EntityTimestamps.module.css';

export const entityStatus = {
  live: 'live',
  completed: 'completed',
  archived: 'archived'
} as const;
export type EntityStatus = (typeof entityStatus)[keyof typeof entityStatus];

/** Timestamp fields of a campaign or event: plain values are UTC, Local values carry the organisation's wall clock. */
export interface EntityTimestampsSource {
  status?: string | null;
  createdAt?: string | null;
  createdAtLocal?: string | null;
  lastUpdatedAt?: string | null;
  lastUpdatedAtLocal?: string | null;
  lastStatusChangedAt?: string | null;
  lastStatusChangedAtLocal?: string | null;
}

interface EntityTimestampsProps {
  entity: EntityTimestampsSource;
}

interface TimestampCandidate {
  key: string;
  label: string;
  at?: string | null;
  atLocal?: string | null;
  Icon: LucideIcon;
}

interface TimestampItem extends TimestampCandidate {
  at: string;
  atLocal: string;
}

const translationBase = 'campaign.performance.header.timestamps';
const relativeWindowDays = 7;

const isKnownStatus = (value: unknown): value is EntityStatus =>
  typeof value === 'string' &&
  Object.values(entityStatus).includes(value as EntityStatus);

/** Relative time inside a week, otherwise the organisation's local date, with the full local date-time as the tooltip. */
const describeWhen = (at: string, atLocal: string) => {
  const instant = apiInstant(at);
  const wallClock = apiWallClock(atLocal);
  const isRecent = dayjs().diff(instant, 'day') <= relativeWindowDays;

  return {
    text: isRecent
      ? formatTimeElapsed(instant)
      : formatDateTime(wallClock, { includeTime: false }),
    title: formatDateTime(wallClock),
    iso: instant.toISOString()
  };
};

/** One line of "what happened, when" for a campaign or event: created, updated, last status change. */
export const EntityTimestamps: React.FC<EntityTimestampsProps> = ({
  entity
}) => {
  const candidates: (TimestampCandidate | null)[] = [
    {
      key: 'created',
      label: I18n.t(`${translationBase}.created`),
      at: entity.createdAt,
      atLocal: entity.createdAtLocal,
      Icon: CalendarPlus
    },
    {
      key: 'updated',
      label: I18n.t(`${translationBase}.updated`),
      at: entity.lastUpdatedAt,
      atLocal: entity.lastUpdatedAtLocal,
      Icon: Clock
    },
    isKnownStatus(entity.status)
      ? {
          key: 'status',
          label: I18n.t(`${translationBase}.status.${entity.status}`),
          at: entity.lastStatusChangedAt,
          atLocal: entity.lastStatusChangedAtLocal,
          Icon: History
        }
      : null
  ];

  const items = candidates.flatMap((candidate): TimestampItem[] => {
    const at = candidate?.at ?? candidate?.atLocal;
    if (!candidate || !at) {
      return [];
    }
    return [{ ...candidate, at, atLocal: candidate.atLocal ?? at }];
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <ul className={styles.list} aria-label={I18n.t(`${translationBase}.label`)}>
      {items.map(({ key, label, at, atLocal, Icon }) => {
        const when = describeWhen(at, atLocal);
        return (
          <li key={key} className={styles.item} title={when.title}>
            <Icon className={styles.icon} aria-hidden="true" />
            <span>{label}</span>
            <time dateTime={when.iso}>
              {when.text}
              <span className={styles.srOnly}>, {when.title}</span>
            </time>
          </li>
        );
      })}
    </ul>
  );
};

export default EntityTimestamps;
